import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import {
    BehaviorSubject,
    Observable,
    catchError,
    filter,
    switchMap,
    take,
    throwError,
} from 'rxjs';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);

    return next(req).pipe(
        catchError((error) => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
                return handle401Error(req, next, authService, error);
            }
            return throwError(() => error);
        })
    );
};

function handle401Error(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    authService: AuthService,
    error: HttpErrorResponse
): Observable<HttpEvent<unknown>> {
    const url = req.url;

    // A 401 while the access token is still valid is NOT a real session expiry
    // (e.g. a backend error mis-classified as 401). Surface the error so the
    // caller can show its message, instead of forcing a logout + reload.
    if (
        authService.accessToken &&
        !AuthUtils.isTokenExpired(authService.accessToken)
    ) {
        return throwError(() => error);
    }

    // Avoid refresh loops: if the failing request is auth-related, sign out
    if (
        url.includes('User/RefreshToken') ||
        url.includes('User/Login') ||
        url.includes('User/Logout') ||
        !authService.refreshToken
    ) {
        authService.signOut();
        location.reload();
        return throwError(() => error);
    }

    if (!isRefreshing) {
        isRefreshing = true;
        refreshTokenSubject.next(null);

        return authService.refreshAccessToken().pipe(
            switchMap((session) => {
                isRefreshing = false;
                refreshTokenSubject.next(session.token);
                return next(addTokenToRequest(req, session.token));
            }),
            catchError((err) => {
                isRefreshing = false;
                authService.signOut();
                location.reload();
                return throwError(() => err);
            })
        );
    }

    // Another request is already refreshing; wait for the new token
    return refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => next(addTokenToRequest(req, token!)))
    );
}

function addTokenToRequest(
    req: HttpRequest<unknown>,
    token: string
): HttpRequest<unknown> {
    return req.clone({
        headers: req.headers.set('Authorization', 'Bearer ' + token),
    });
}
